import { useCallback, useEffect, useMemo, useState } from 'react';
import { uniqBy } from 'lodash-es';

import { useFallbackTranslation } from '@gn-audio/jss-components';
import { Option } from '@gn-audio/jss-core/common/models';

import {
  CaseStudiesFilterOption,
  CaseStudiesFilterProps,
  CaseStudiesFilterType,
  CaseStudyTileProps,
} from './caseStudiesTypes';
const CaseStudiesFilter = ({
  caseStudies,
  productLabel,
  industryLabel,
  softwareLabel,
  buttonLabel,
  onFilterChange,
}: CaseStudiesFilterProps) => {
  const { t } = useFallbackTranslation();
  const [activeFilter, setActiveFilter] = useState<CaseStudiesFilterType>('showall');
  const [selectedValue, setSelectedValue] = useState<string>('');

  const createFilterOptions = useCallback((items: CaseStudiesFilterOption[]): Option[] => {
    return uniqBy(
      items.filter((item) => item?.id && item?.value?.value && item?.label?.value),
      'id'
    ).map((item) => ({
      key: item.id,
      title: item.label.value,
      value: item.value.value,
    }));
  }, []);

  const filterOptions = useMemo(
    () => ({
      product: createFilterOptions(caseStudies.flatMap((study) => study.productCategories || [])),
      industry: createFilterOptions(caseStudies.flatMap((study) => study.industries || [])),
      software: createFilterOptions(caseStudies.flatMap((study) => study.software || [])),
    }),
    [caseStudies, createFilterOptions]
  );

  const filteredCaseStudies = useMemo(() => {
    if (activeFilter === 'showall') return caseStudies;

    const filterMap = {
      product: (study: CaseStudyTileProps) =>
        study.productCategories?.value?.value === selectedValue,
      industry: (study: CaseStudyTileProps) => study.industries?.value?.value === selectedValue,
      software: (study: CaseStudyTileProps) => study.software?.value?.value === selectedValue,
    };

    return caseStudies.filter(filterMap[activeFilter]);
  }, [activeFilter, selectedValue, caseStudies]);

  useEffect(() => {
    onFilterChange(filteredCaseStudies);
  }, [filteredCaseStudies, onFilterChange]);

  const handleFilterChange = useCallback(
    (filterType: CaseStudiesFilterType, value: string = '') => {
      setActiveFilter(filterType);
      setSelectedValue(value);
    },
    []
  );

  const getFilterClass = useCallback((isSelected: boolean) => {
    const baseClass =
      'w-max cursor-pointer outline-none focus-within:outline-none focus-within:shadow-[0_0_0_3px_#87cefa] rounded-full px-4 py-2 text-center text-sm';
    const bgClass = isSelected ? 'bg-green-700 text-white' : 'bg-transparent text-black';
    return `${baseClass} ${bgClass}`;
  }, []);

  const renderSelect = (type: CaseStudiesFilterType, label: string, options: Option[]) => {
    if (!options.length) return null;

    return (
      <select
        title={label}
        value={activeFilter === type ? selectedValue : label}
        onChange={(e) => handleFilterChange(type, e.target.value)}
        className={getFilterClass(activeFilter === type)}
      >
        <option value={label} hidden disabled>
          {label}
        </option>
        {options.map((option) => (
          <option key={option.key} value={option.value} className="bg-white text-black">
            {option.title}
          </option>
        ))}
      </select>
    );
  };

  return (
    <div className="mb-6 flex overflow-x-auto md:justify-center md:overflow-hidden">
      <div className="flex gap-3 rounded-full bg-[#eff1f1] p-3">
        <button
          aria-pressed={activeFilter === 'showall'}
          className={getFilterClass(activeFilter === 'showall')}
          onClick={() => handleFilterChange('showall')}
          aria-label={t('system.showAll') || 'Show All'}
        >
          {buttonLabel || 'Show All'}
        </button>
        {renderSelect('product', productLabel || 'Product', filterOptions.product)}
        {renderSelect('industry', industryLabel || 'Industry', filterOptions.industry)}
        {renderSelect('software', softwareLabel || 'Software', filterOptions.software)}
      </div>
    </div>
  );
};
export default CaseStudiesFilter;
